import type { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { route } from 'ziggy-js';

// Host-relative on purpose: `login` route name is pinned to the
// central domain, but this interceptor also runs on tenants'
// subdomains, which serve `tenant.login` on the same path.
const loginPath = (): string => route('login', undefined, false);

interface RetryableConfig extends InternalAxiosRequestConfig {
    _csrfRetry?: boolean;
}

let isRefreshing = false;
let failedQueue: Array<{
    resolve: (value: unknown) => void;
    reject: (reason: unknown) => void;
    config: RetryableConfig;
}> = [];

const processQueue = (error: AxiosError | null, axiosInstance: AxiosInstance): void => {
    failedQueue.forEach(({ resolve, reject, config }) => {
        if (error) {
            reject(error);
        } else {
            resolve(axiosInstance.request(config));
        }
    });
    failedQueue = [];
};

export function setupCsrfInterceptor(axiosInstance: AxiosInstance): void {
    axiosInstance.interceptors.response.use(
        (response) => response,
        async (error: AxiosError) => {
            const originalConfig = error.config as RetryableConfig | undefined;

            if (!originalConfig || error.response?.status !== 419) {
                return Promise.reject(error);
            }

            // Ensure we don't loop forever
            if (originalConfig._csrfRetry) {
                window.location.href = loginPath();
                return Promise.reject(error);
            }

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject, config: originalConfig });
                });
            }

            originalConfig._csrfRetry = true;
            isRefreshing = true;

            try {
                await axiosInstance.get('/sanctum/csrf-cookie', { withCredentials: true });
                processQueue(null, axiosInstance);
                return axiosInstance.request(originalConfig);
            } catch (refreshError) {
                processQueue(refreshError as AxiosError, axiosInstance);
                window.location.href = loginPath();
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }
    );
}
